import { Box } from '@mui/material';
import classNames from 'classnames';
import React, { memo } from 'react';

import { createMixins } from '../../../styles/mui/base/create-mixins';
import { createTokens } from '../../../styles/mui/base/create-tokens';

const tokens = createTokens();
const mixins = createMixins(tokens);

// Marker classes so the root button's :hover can target the descendant
// details/caret elements (the original CSS used `& .details` / `& .caret`).
const DETAILS_CLASS = 'SearchMapGroupLabel-details';
const CARET_CLASS = 'SearchMapGroupLabel-caret';

const rootSx = {
  // Size from content
  position: 'relative',
  width: 'auto',
  height: 'auto',
  padding: 0,
  border: 0,

  '&:hover': {
    [`& .${DETAILS_CLASS}, & .${CARET_CLASS}`]: {
      cursor: 'pointer',

      // Same as active
      backgroundColor: tokens.marketplaceColor,
      borderColor: tokens.marketplaceColor,
      color: tokens.matterColorLight,
      boxShadow: tokens.boxShadowPopup,
    },
  },
};

const detailsSx = {
  // Since caret is absolutely positioned, label must have relative to be
  // included to the same rendering layer
  position: 'relative',

  // Font
  ...mixins.marketplaceDefaultFontStyles,
  fontWeight: tokens.fontWeightSemiBold,
  marginTop: 0,
  marginBottom: 0,

  padding: '6px 14px',

  // Coloring
  backgroundColor: tokens.matterColorLight,
  color: tokens.matterColor,

  // Borders
  borderRadius: '17px',
  borderStyle: 'solid',
  borderColor: tokens.matterColorNegative,
  borderWidth: '1px',
  boxShadow: tokens.boxShadowPopupLight,

  // Property-specific transition; catch-all `all` transitions make the
  // browser track transition state every time Mapbox writes a marker
  // transform during pan — keep this to the 4 props that actually
  // animate between default / hover / active states.
  transition:
    'background-color 0.1s ease-in-out, border-color 0.1s ease-in-out, color 0.1s ease-in-out, box-shadow 0.1s ease-in-out',

  // Overwrite dimensions from font styles
  '@media (min-width:900px)': {
    marginTop: 0,
    marginBottom: 0,
  },
};

const detailsActiveSx = {
  // Same as hover
  backgroundColor: tokens.marketplaceColor,
  borderColor: tokens.marketplaceColor,
  color: tokens.matterColorLight,
  boxShadow: tokens.boxShadowPopup,
};

const caretShadowSx = {
  // Caret / arrow dimensions and position
  width: '6px',
  height: '6px',
  position: 'absolute',
  bottom: '-3px',
  left: '50%',
  marginLeft: '-3px',
  transform: 'rotate(45deg)',

  // Caret should have same box-shadow as label
  boxShadow: tokens.boxShadowPopupLight,
};

const caretSx = {
  // Caret / arrow dimensions and position
  width: '6px',
  height: '6px',
  position: 'absolute',
  bottom: '-3px',
  left: '50%',
  marginLeft: '-3px',
  transform: 'rotate(45deg)',

  // Caret should have same bg-color as label
  backgroundColor: tokens.matterColorLight,

  transition: 'background-color 0.1s ease-in-out, border-color 0.1s ease-in-out',
};

const caretActiveSx = {
  // Same as hover
  backgroundColor: tokens.marketplaceColor,
  borderColor: tokens.marketplaceColor,
};

/**
 * @param {Object} props
 * @param {string} [props.className]
 * @param {string} [props.rootClassName]
 * @param {Array} props.listings
 * @param {Function} props.onListingClicked
 * @param {boolean} props.isActive
 * @param {*} [props.mapComponentRefreshToken]
 */
const SearchMapGroupLabelComponent = ({
  className = null,
  rootClassName = null,
  listings,
  onListingClicked,
  isActive,
}) => {
  const classes = classNames(className);
  const countLabelClasses = classNames(DETAILS_CLASS);
  const caretClasses = classNames(CARET_CLASS);

  return (
    <Box
      className={classes}
      component="button"
      onClick={() => onListingClicked(listings)}
      sx={rootClassName ? undefined : rootSx}
    >
      <Box sx={caretShadowSx} />
      <Box
        className={countLabelClasses}
        sx={{ ...detailsSx, ...(isActive ? detailsActiveSx : {}) }}
      >
        {listings.length}
      </Box>
      <Box
        className={caretClasses}
        sx={{ ...caretSx, ...(isActive ? caretActiveSx : {}) }}
      />
    </Box>
  );
};

// Preserves the original shouldComponentUpdate behaviour: only re-render when the
// listing count, active status, or map refresh token changes. memo's comparator
// returns true to skip rendering (i.e. when props are considered equal).
const arePropsEqual = (prevProps, nextProps) =>
  prevProps.listings.length === nextProps.listings.length &&
  prevProps.isActive === nextProps.isActive &&
  prevProps.mapComponentRefreshToken === nextProps.mapComponentRefreshToken;

const SearchMapGroupLabel = memo(SearchMapGroupLabelComponent, arePropsEqual);

export default SearchMapGroupLabel;
